import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectIsCurrentUrlIndexable } from 'src/redux/content/selectors/selectIsCurrentUrlIndexable';
import { selectResolvedPathname } from 'src/redux/navigation/selectors';
import { getUrlFilterValues } from '../helpers/getUrlFilterValues';
import { levelsToPageCategories, toBreadcrumb } from '../helpers/pageCategories';
import { selectRouteParseOptions } from './selectRouteParseOptions';

/**
 * The single source of truth for the page's `pageCategories` value — the one the
 * listings-api request ({@link adaptStoreToRequestBody}) and the dataLayer
 * `crs_categories` ({@link getServerSidePageEventObject}) both read, so they
 * cannot diverge. It is the only place the value is assembled.
 *
 * Off the taxonomy-shop route (CLP/PPLP this is the value set at SSR-init from
 * the URL path; Search has none) it is returned verbatim. On the taxonomy-shop
 * route it is built from the active facet selection ({@link levelsToPageCategories}),
 * with the URL's filter values ({@link getUrlFilterValues}) appended (lower-cased)
 * — but only when the current URL is an **exact**, indexable content match
 * (selection rule 1 + indexability, via {@link selectIsCurrentUrlIndexable}).
 * Filter inclusion is independent of which entry's content renders: a long-tail
 * URL that merely borrows an ancestor's content gets no appended filters, and a
 * surface root with no active taxonomy level never becomes a filter-only value.
 */
export const selectPageCategories = (state: ReduxApplicationState): string[] | undefined => {
  const parseOptions = selectRouteParseOptions(state);

  // Non-taxonomy routes (CLP/PPLP/SLP): the value is set once at SSR-init, or absent.
  if (!parseOptions) {
    return state.productsQuery.pageCategories;
  }

  const levels = levelsToPageCategories(
    state.productsQuery.facets,
    state.productsQuery.taxonomyAttributeAliases,
  );
  const baseBreadcrumb = levels[0];

  // Filters only ever *extend* a product-type breadcrumb, and only on an exact
  // indexable match — otherwise the product-types-only breadcrumb stands.
  if (!baseBreadcrumb || !selectIsCurrentUrlIndexable(state)) {
    return levels;
  }

  const filterValues = getUrlFilterValues(selectResolvedPathname(state), parseOptions);

  if (filterValues.length === 0) {
    return levels;
  }

  return [toBreadcrumb([baseBreadcrumb, ...filterValues])];
};
